import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2, FileText, Upload, Trash2 } from 'lucide-react';
import { brl, fmtDay, openReceipt } from './EditorFinance';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const linkBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '0.78rem', fontWeight: 600 };

const ReceiptControls = ({ ev, onChange }) => {
  const input = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const url = `${API}/admin/finance/payouts/${ev.payout_id}/receipt`;
  const upload = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) { alert('Envie o comprovante em PDF.'); return; }
    setBusy(true);
    const body = new FormData();
    body.append('file', file);
    try { await axios.post(url, body, { headers: authHeaders() }); onChange(); }
    catch (e) { alert(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Não foi possível enviar o comprovante.'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Remover o comprovante deste repasse?')) return;
    setBusy(true);
    try { await axios.delete(url, { headers: authHeaders() }); onChange(); }
    catch (e) { alert('Não foi possível remover o comprovante.'); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`receipt-controls-${ev.payout_id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
      <input ref={input} type="file" accept="application/pdf,.pdf" hidden data-testid={`receipt-input-${ev.payout_id}`} onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }} />
      {busy ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#68AAB0' }} /> : ev.receipt ? (<>
        <button type="button" style={{ ...linkBtn, color: '#2c7a7b' }} onClick={() => openReceipt(url, authHeaders())} data-testid={`receipt-view-${ev.payout_id}`}><FileText className="w-3.5 h-3.5" />{ev.receipt.filename}</button>
        <button type="button" style={{ ...linkBtn, color: '#4a5568' }} onClick={() => input.current.click()} data-testid={`receipt-replace-${ev.payout_id}`}><Upload className="w-3.5 h-3.5" />Substituir</button>
        <button type="button" style={{ ...linkBtn, color: '#c53030' }} onClick={remove} data-testid={`receipt-remove-${ev.payout_id}`}><Trash2 className="w-3.5 h-3.5" />Remover</button>
      </>) : (
        <button type="button" style={{ ...linkBtn, color: '#2c7a7b' }} onClick={() => input.current.click()} data-testid={`receipt-upload-${ev.payout_id}`}><Upload className="w-3.5 h-3.5" />Enviar comprovante (PDF)</button>
      )}
    </div>
  );
};
const card = { background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: '1.25rem' };
const STATUS = {
  paid: { bg: '#c6f6d5', fg: '#22543d', label: 'Pago' },
  today: { bg: '#fed7d7', fg: '#9b2c2c', label: 'Hoje' },
  overdue: { bg: '#feebc8', fg: '#9c4221', label: 'Atrasado' },
  scheduled: { bg: '#bee3f8', fg: '#2a4365', label: 'Agendado' }
};
const accountText = (c) => (!c ? 'Dados de recebimento não informados' : [c.titular, c.pix && `PIX ${c.pix}`, c.banco && `Banco ${c.banco} · Ag ${c.agencia} · Cc ${c.conta}`].filter(Boolean).join(' · '));

const markPaid = async (ev, onDone) => {
  if (!window.confirm(`Confirmar que o repasse de ${brl(ev.amount)} para ${ev.nome} (${fmtDay(ev.date)}) foi pago?`)) return;
  try {
    await axios.post(`${API}/admin/finance/payouts`, { editor_id: ev.editor_id, date: ev.date }, { headers: authHeaders() });
    onDone();
  } catch (e) {
    alert(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Erro ao registrar repasse');
  }
};

export const PayoutAlerts = ({ alerts, onPaid }) => (alerts?.length ? (
  <div data-testid="payout-alerts" style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
    <p style={{ fontWeight: 700, color: '#9b2c2c', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}><AlertTriangle className="w-4 h-4" />Repasses para fazer</p>
    {alerts.map((a) => (
      <div key={a.editor_id + a.date} data-testid={`payout-alert-${a.editor_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.4rem 0', borderTop: '1px solid #fed7d7', fontSize: '0.85rem' }}>
        <span><strong>{a.nome}</strong> · {brl(a.amount)} · {a.status === 'today' ? 'hoje' : `atrasado desde ${fmtDay(a.date)}`}<br /><span style={{ color: '#718096' }}>{accountText(a.conta)}</span></span>
        {onPaid && <Button size="sm" onClick={() => markPaid(a, onPaid)} data-testid={`payout-alert-pay-${a.editor_id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Marcar pago</Button>}
      </div>
    ))}
  </div>
) : null);

const PercentCell = ({ row, onSaved }) => {
  const [v, setV] = useState(String(row.percentual_plataforma));
  const dirty = Number(v) !== row.percentual_plataforma;
  const save = async () => {
    try {
      await axios.put(`${API}/admin/editors/${row.editor_id}/platform-percent`, { percentual: Number(v) }, { headers: authHeaders() });
      alert(`Percentual de ${row.nome} atualizado para ${Number(v)}%.`);
      onSaved();
    } catch (e) {
      alert('Percentual inválido (0 a 100)');
    }
  };
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <Input type="number" min={0} max={100} value={v} onChange={(e) => setV(e.target.value)} style={{ width: 70, height: 32 }} data-testid={`platform-pct-input-${row.editor_id}`} />%
      {dirty && <Button size="sm" onClick={save} data-testid={`platform-pct-save-${row.editor_id}`}>Salvar</Button>}
    </div>
  );
};

const Calendar = ({ month, events, today, onMonth, onPick, selected }) => {
  const [y, m] = month.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const days = new Date(y, m, 0).getDate();
  const cells = Array(first.getDay()).fill(null).concat(Array.from({ length: days }, (_, i) => i + 1));
  const iso = (d) => `${month}-${String(d).padStart(2, '0')}`;
  const shift = (n) => { const d = new Date(y, m - 1 + n, 1); onMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); };
  return (
    <div style={card} data-testid="finance-calendar">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <Button variant="ghost" size="sm" onClick={() => shift(-1)} data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <strong style={{ textTransform: 'capitalize' }} data-testid="calendar-month">{first.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</strong>
        <Button variant="ghost" size="sm" onClick={() => shift(1)} data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, fontSize: '0.75rem' }}>
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((d) => <div key={d} style={{ textAlign: 'center', color: '#a0aec0', fontWeight: 600 }}>{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const evs = events.filter((e) => e.date === iso(d));
          const st = evs.length ? STATUS[evs.find((e) => e.status !== 'paid')?.status || 'paid'] : null;
          return (
            <button key={d} type="button" onClick={() => onPick(iso(d))} data-testid={`calendar-day-${iso(d)}`}
              style={{ minHeight: 54, borderRadius: 8, border: selected === iso(d) ? '2px solid #3182ce' : iso(d) === today ? '2px solid #2D3748' : '1px solid #edf2f7', background: st ? st.bg : 'white', color: st ? st.fg : '#4a5568', cursor: 'pointer', padding: 4, textAlign: 'left', fontSize: '0.75rem' }}>
              <div style={{ fontWeight: 600 }}>{d}</div>
              {evs.length > 0 && <div style={{ fontSize: '0.65rem' }}>{brl(evs.reduce((t, e) => t + e.amount, 0))}</div>}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap', fontSize: '0.7rem' }}>
        {Object.values(STATUS).map((s) => <span key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: s.bg }} />{s.label}</span>)}
      </div>
    </div>
  );
};

export const AdminFinance = () => {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/admin/finance?month=${month}`, { headers: authHeaders() }).then((r) => setData(r.data)).catch(() => setData(false));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  if (data === null) return <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (data === false) return <p>Erro ao carregar o financeiro.</p>;
  const t = data.totals;
  const dayEvents = data.events.filter((e) => e.date === selected);
  const totalCards = [
    ['Assinaturas vendidas', t.gross, 'admin-fin-gross'], ['Fica com a plataforma', t.platform_fee, 'admin-fin-fee'],
    ['Devido às editoras', t.editor_net, 'admin-fin-net'], ['Já repassado', t.paid_out, 'admin-fin-paid'],
    ['Liberado para repasse', t.available, 'admin-fin-available'], ['Em carência', t.in_grace, 'admin-fin-grace']
  ];
  return (
    <div className="financial-page" data-testid="admin-finance">
      <div className="page-header"><h1 className="page-title">Financeiro</h1></div>
      <PayoutAlerts alerts={data.alerts} onPaid={load} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {totalCards.map(([l, v, id]) => (
          <div key={id} style={card}><p style={{ fontSize: '0.75rem', color: '#718096' }}>{l}</p><p style={{ fontSize: '1.2rem', fontWeight: 700, color: '#2D3748' }} data-testid={id}>{brl(v)}</p></div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1.2fr) minmax(280px, 1fr)', gap: '1.25rem', marginBottom: '1.25rem' }}>
        <Calendar month={month} events={data.events} today={data.today} onMonth={(mo) => { setMonth(mo); setSelected(null); }} onPick={setSelected} selected={selected} />
        <div style={card} data-testid="calendar-day-details">
          <h3 style={{ fontWeight: 600, color: '#2D3748', marginBottom: '0.75rem' }}>{selected ? `Repasses de ${fmtDay(selected)}` : 'Repasses do mês'}</h3>
          {(selected ? dayEvents : data.events).length === 0 ? <p style={{ fontSize: '0.85rem', color: '#a0aec0' }}>Nenhum repasse.</p> :
            (selected ? dayEvents : data.events).map((e) => (
              <div key={e.editor_id + e.date + e.status} style={{ padding: '0.6rem 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span><strong>{e.nome}</strong> · {fmtDay(e.date)}</span>
                  <span style={{ background: STATUS[e.status].bg, color: STATUS[e.status].fg, padding: '2px 8px', borderRadius: 50, fontSize: '0.7rem' }}>{STATUS[e.status].label}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                  <span>{brl(e.amount)}{e.status !== 'paid' && <span style={{ color: '#718096' }}> · {accountText(e.conta)}</span>}</span>
                  {e.status !== 'paid' && e.status !== 'scheduled' && <Button size="sm" variant="outline" onClick={() => markPaid(e, load)} data-testid={`event-pay-${e.editor_id}-${e.date}`}>Marcar pago</Button>}
                </div>
                {e.status === 'paid' && e.payout_id && <ReceiptControls ev={e} onChange={load} />}
              </div>
            ))}
        </div>
      </div>
      <div style={card}>
        <h3 style={{ fontWeight: 600, color: '#2D3748', marginBottom: '0.75rem' }}>Por editora</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }} data-testid="admin-finance-editors">
            <thead><tr>{['Editora', 'Plataforma %', 'Editora %', 'Vendido', 'Plataforma', 'Editora', 'Repassado', 'Liberado', 'Carência', 'Próximo repasse', 'Dia'].map((h, i) => <th key={`${i}-${h}`} style={{ textAlign: 'left', padding: '0.5rem', color: '#718096', borderBottom: '1px solid #e2e8f0' }}>{h}</th>)}</tr></thead>
            <tbody>
              {data.editors.map((r) => (
                <tr key={r.editor_id} data-testid={`admin-finance-row-${r.editor_id}`}>
                  <td style={{ padding: '0.5rem' }}><strong>{r.nome}</strong><br /><span style={{ color: '#718096' }}>{r.plataforma_publica ? 'Pública (sem cobrança)' : `${brl(r.valor_assinatura)}/mês`}</span></td>
                  <td style={{ padding: '0.5rem' }}><PercentCell row={r} onSaved={load} /></td>
                  <td style={{ padding: '0.5rem' }}>{100 - r.percentual_plataforma}%</td>
                  <td style={{ padding: '0.5rem' }}>{brl(r.gross)}</td>
                  <td style={{ padding: '0.5rem' }}>{brl(r.platform_fee)}</td>
                  <td style={{ padding: '0.5rem' }}>{brl(r.editor_net)}</td>
                  <td style={{ padding: '0.5rem' }}>{brl(r.paid_out)}</td>
                  <td style={{ padding: '0.5rem' }}>{brl(r.available)}</td>
                  <td style={{ padding: '0.5rem' }}>{brl(r.in_grace)}</td>
                  <td style={{ padding: '0.5rem' }}>{r.next_payout_amount ? `${fmtDay(r.next_payout_date)} · ${brl(r.next_payout_amount)}` : '—'}</td>
                  <td style={{ padding: '0.5rem' }}>{r.payout_day}º útil</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: '0.72rem', color: '#a0aec0', marginTop: 8 }}>O percentual vale para as próximas assinaturas pagas; as já registradas mantêm o percentual da data do pagamento.</p>
      </div>
    </div>
  );
};

export const AdminPayoutBanner = () => {
  const [alerts, setAlerts] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/admin/finance`, { headers: authHeaders() }).then((r) => setAlerts(r.data.alerts)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return <PayoutAlerts alerts={alerts} onPaid={load} />;
};
